import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Users, RefreshCw, Shield, TrendingUp, 
  UserX, UserCheck, Activity, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/api';

const ConflictStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getConflictStats();
      setStats(response.data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 w-48 bg-slate-200 rounded" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-slate-100 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <p className="text-red-600 text-sm">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  return (
    <Card data-testid="conflict-stats-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-600" />
            Estatísticas de Conflitos de Interesse
          </CardTitle>
          <CardDescription>
            Visão geral das deteções e reatribuições automáticas
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchStats} data-testid="refresh-stats-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
            <div className="flex items-center gap-2 text-amber-700 mb-1">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium uppercase">Total Alertas</span>
            </div>
            <p className="text-2xl font-bold text-amber-900">{stats.total_alerts}</p>
            <p className="text-xs text-amber-600 mt-1">
              {stats.recent_alerts_7d} nos últimos 7 dias
            </p>
          </div>

          <div className="bg-red-50 rounded-lg p-4 border border-red-100">
            <div className="flex items-center gap-2 text-red-700 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium uppercase">Pendentes</span>
            </div>
            <p className="text-2xl font-bold text-red-900">{stats.by_status?.pending || 0}</p>
            <p className="text-xs text-red-600 mt-1">
              Aguardam resolução
            </p>
          </div>

          <div className="bg-green-50 rounded-lg p-4 border border-green-100">
            <div className="flex items-center gap-2 text-green-700 mb-1">
              <UserCheck className="w-4 h-4" />
              <span className="text-xs font-medium uppercase">Resolvidos</span>
            </div>
            <p className="text-2xl font-bold text-green-900">{stats.by_status?.resolved || 0}</p>
            <p className="text-xs text-green-600 mt-1">
              Reatribuídos com sucesso
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-2 text-slate-700 mb-1">
              <Shield className="w-4 h-4" />
              <span className="text-xs font-medium uppercase">Regras Ativas</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats.active_rules}</p>
            <p className="text-xs text-slate-600 mt-1">
              Configuradas
            </p>
          </div>
        </div>

        {/* AI vs Rules Detection */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-100">
            <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Deteção por Tipo
            </h4>
            <div className="flex gap-4">
              <div className="flex-1">
                <p className="text-xs text-purple-600 uppercase">Detetados pela IA</p>
                <p className="text-xl font-bold text-purple-900">{stats.by_type?.ai_detected || 0}</p>
              </div>
              <div className="flex-1">
                <p className="text-xs text-purple-600 uppercase">Detetados por Regras</p>
                <p className="text-xl font-bold text-purple-900">{stats.by_type?.rule_detected || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg p-4 border border-teal-100">
            <h4 className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Reatribuições Automáticas
            </h4>
            <div className="flex gap-4">
              <div className="flex-1">
                <p className="text-xs text-teal-600 uppercase">Total</p>
                <p className="text-xl font-bold text-teal-900">{stats.reassignments?.total || 0}</p>
              </div>
              <div className="flex-1">
                <p className="text-xs text-teal-600 uppercase">Pela IA</p>
                <p className="text-xl font-bold text-teal-900">{stats.reassignments?.by_ai || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Top Lists */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Top Blocked Users */}
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <UserX className="w-4 h-4 text-red-500" />
              Utilizadores Mais Bloqueados
            </h4>
            {stats.top_blocked_users?.length > 0 ? (
              <ul className="space-y-2">
                {stats.top_blocked_users.map((user, index) => (
                  <li key={index} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{user.name}</span>
                    <Badge variant="secondary">{user.count} alertas</Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">Sem dados disponíveis</p>
            )}
          </div>

          {/* Top Fallback Users */}
          <div className="border rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-green-500" />
              Utilizadores Substitutos Mais Usados
            </h4>
            {stats.top_fallback_users?.length > 0 ? (
              <ul className="space-y-2">
                {stats.top_fallback_users.map((user, index) => (
                  <li key={index} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{user.name}</span>
                    <Badge variant="outline" className="border-green-200 text-green-700">
                      {user.count} atribuições
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">Sem dados disponíveis</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConflictStatsPanel;
